import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const PayPalCreditButton = ({ researcherId, creditsAmount, onSuccess, onCancel }) => {
  const paypalRef = useRef();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  // Credit packages mapping
  const CREDIT_PACKAGES = {
    50: { credits: 50, price: 5.00 },
    100: { credits: 100, price: 10.00 },
    200: { credits: 200, price: 20.00 }
  };

  const packageInfo = CREDIT_PACKAGES[creditsAmount];

  useEffect(() => {
    // Load PayPal script
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD&intent=capture`;
    script.addEventListener('load', () => setScriptLoaded(true));
    document.body.appendChild(script);

    return () => {
      // Cleanup: remove script when component unmounts
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (scriptLoaded && paypalRef.current && window.paypal) {
      setIsLoading(true);
      
      window.paypal.Buttons({
        style: {
          layout: 'vertical',
          color: 'gold',
          shape: 'rect',
          label: 'paypal'
        },
        
        createOrder: async () => {
          try {
            const response = await fetch(`${BACKEND_URL}/api/paypal/create-credit-order`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                researcher_id: researcherId,
                credits_amount: creditsAmount,
                return_url: `${window.location.origin}/dashboard?paypal_success=true`,
                cancel_url: `${window.location.origin}/dashboard?paypal_cancel=true`
              })
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.detail || 'Failed to create order');
            }

            const data = await response.json();
            return data.order_id;
          } catch (error) {
            console.error('Error creating order:', error);
            toast({
              title: t('error') || "Error",
              description: error.message,
              variant: "destructive"
            });
            throw error;
          }
        },

        onApprove: async (data) => {
          try {
            toast({
              title: t('processingPayment') || "Processing Payment",
              description: t('pleaseWait') || "Please wait...",
            });

            const response = await fetch(`${BACKEND_URL}/api/paypal/capture-credit-order`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                order_id: data.orderID,
                researcher_id: researcherId
              })
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.detail || 'Failed to capture payment');
            }

            const captureData = await response.json();
            
            toast({
              title: t('purchaseSuccessful') || "Purchase Successful!",
              description: `${captureData.credits_added} ${t('creditsAdded') || 'credits added'}`,
              variant: "default"
            });

            if (onSuccess) {
              onSuccess(captureData);
            }
          } catch (error) {
            console.error('Error capturing payment:', error);
            toast({
              title: t('paymentFailed') || "Payment Failed",
              description: error.message,
              variant: "destructive"
            });
          }
        },

        onCancel: () => {
          toast({
            title: t('paymentCancelled') || "Payment Cancelled",
            description: t('paymentCancelledDesc') || "You cancelled the payment.",
            variant: "default"
          });
          
          if (onCancel) {
            onCancel();
          }
        },

        onError: (err) => {
          console.error('PayPal error:', err);
          toast({
            title: t('error') || "Error",
            description: "An error occurred with PayPal. Please try again.",
            variant: "destructive"
          });
        }
      }).render(paypalRef.current)
      .then(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error rendering PayPal buttons:', error);
        setIsLoading(false);
      });
    }
  }, [scriptLoaded, creditsAmount, researcherId]);

  if (!packageInfo) {
    return (
      <div className="text-center text-red-600 p-4">
        Invalid credit package selected
      </div>
    );
  }

  return (
    <div className="w-full">
      {isLoading && (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
        </div>
      )}
      <div ref={paypalRef} className={isLoading ? 'opacity-0' : 'opacity-100'}></div>
      
      {!isLoading && (
        <p className="text-xs text-center text-gray-500 mt-3">
          💳 Click "Debit or Credit Card" to pay without a PayPal account
        </p>
      )}
    </div>
  );
};

export default PayPalCreditButton;
